import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { AuthoringStatus } from '@/components/patterns/StatusPill';
import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { questionnaireKeys } from './questionnaireKeys';

export interface BulkResult {
  total: number;
  succeeded: number;
  failed: number;
}

/**
 * Sets many questionnaires to one status.
 *
 * Fans out over the existing per-questionnaire endpoint with `Promise.allSettled`, so one failure does
 * not abort the rest and the caller can report "8 updated, 2 failed" instead of an all-or-nothing
 * error. Same approach as `useBulkWorkflowStatus`, and for the same reason: a real bulk endpoint would
 * have to define its own partial-failure semantics, and there is nothing here that needs to be atomic
 * — each questionnaire's status is independent of the others'.
 *
 * Rows already at the target are skipped rather than re-sent, so "set 10 to Draft" when 7 are already
 * drafts is three requests.
 */
export function useBulkQuestionnaireStatus() {
  const qc = useQueryClient();
  const tenantId = useTenantStore((s) => s.tenantId);

  return useMutation({
    mutationFn: async ({
      items,
      target,
    }: {
      items: Array<{ id: string; status: string }>;
      target: AuthoringStatus;
    }): Promise<BulkResult> => {
      const pending = items.filter((q) => q.status !== target);

      const results = await Promise.allSettled(
        pending.map((q) =>
          apiClient.post(`api/v1/t/${tenantId}/questionnaires/${q.id}/status`, {
            json: { status: target },
          }),
        ),
      );

      const failed = results.filter((r) => r.status === 'rejected').length;
      // Counted against the SELECTION, not against `pending` — "10 selected, 10 succeeded" is what an
      // admin expects when seven were already in the target state, not "3 succeeded".
      return { total: items.length, succeeded: items.length - failed, failed };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: questionnaireKeys.lists() });
    },
  });
}

/** Deletes many questionnaires. Permanent, and reported per-row like the status fan-out above. */
export function useBulkDeleteQuestionnaires() {
  const qc = useQueryClient();
  const tenantId = useTenantStore((s) => s.tenantId);

  return useMutation({
    mutationFn: async (ids: string[]): Promise<BulkResult> => {
      const results = await Promise.allSettled(
        ids.map((id) => apiClient.delete(`api/v1/t/${tenantId}/questionnaires/${id}`)),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { total: ids.length, succeeded: ids.length - failed, failed };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: questionnaireKeys.lists() });
    },
  });
}
